import { useState } from 'react';
import { ChevronLeft, ChevronRight, BadgePercent, Wallet } from 'lucide-react';
import { getAffiliateUrl } from '../data/affiliateLinks';
import { propFirmLogos } from '../data/propFirmLogos';
import { getBestDiscount } from '../utils/getBestDiscount';

import type { Discount, Challenge } from '../utils/getBestDiscount';

interface FirmFeatures {
  maxFunding: string;
  profitSplit: number;
}

interface Firm {
  id: string;
  name: string;
  features: FirmFeatures;
  discounts?: Discount[];
  pricing?: Challenge[];
}

interface HeroCarouselCardProps {
  firm: Firm;
  onPrev: () => void;
  onNext: () => void;
}

function HeroCarouselCard({ firm, onPrev, onNext }: HeroCarouselCardProps) {
  const [revealCode, setRevealCode] = useState(false);
  if (!firm) return null;

  const promoList = firm.discounts || [];
  const challenge = firm.pricing?.find((p) => p.phases === 2 && p.accountSize === 100000);
  const discount = getBestDiscount(promoList, challenge);

  const originalPrice = challenge?.fee || 0;
  const discountPercent = discount?.percent || 0;
  const discountedPrice = discount ? originalPrice * (1 - discountPercent / 100) : originalPrice;
  const savings = originalPrice - discountedPrice;

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 backdrop-blur-md p-4 sm:p-8 rounded-2xl text-white shadow-lg sm:shadow-xl hover:shadow-xl hover:scale-[1.02] transition-all duration-200 w-full max-w-full sm:max-w-md border border-yellow-600 sm:border-yellow-700">
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
        <div className="bg-black p-2 rounded-lg flex-shrink-0 mb-2 sm:mb-0">
          <img src={propFirmLogos[firm.id]?.logo} alt={firm.name} className="w-16 h-16 object-contain" loading="lazy" />
        </div>
        <div className="flex flex-col items-center sm:items-start w-full">
          <div className="font-bold text-xl sm:text-lg text-yellow-400 mb-1 text-center sm:text-left">{firm.name}</div>
          <div className="flex flex-row gap-4 justify-center sm:justify-start w-full mt-2">
            {/* Max Funding Stat */}
            <div className="flex flex-col items-center sm:items-start flex-1 min-w-[100px]">
              <span className="text-xs text-gray-400 font-medium mb-0.5">Max Funding</span>
              <span className="text-lg sm:text-base font-bold text-yellow-300">${firm.features.maxFunding.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
            </div>
            {/* Divider */}
            <div className="hidden sm:block h-8 border-l border-gray-700 mx-2"></div>
            {/* Profit Split Stat */}
            <div className="flex flex-col items-center sm:items-start flex-1 min-w-[100px]">
              <span className="text-xs text-gray-400 font-medium mb-0.5">Profit Split</span>
              <span className="text-lg sm:text-base font-bold text-blue-300">Up to {firm.features.profitSplit}%</span>
            </div>
          </div>
        </div>
      </div>

      {discount && discountPercent > 0 && (
        <div className="mb-4">
          <div className="bg-yellow-100 rounded-lg p-3 text-yellow-900 flex flex-col items-center shadow-md border border-yellow-300">
            <div className="inline-flex items-center gap-2 font-extrabold text-xl">
              <BadgePercent className="w-6 h-6 text-yellow-700" /> Save {discountPercent}% Today!
            </div>
          </div>
          
          <div className="text-sm sm:text-base space-y-1 text-center mb-6">
            {discount && discountPercent > 0 && (
              <>
                <div className="line-through text-yellow-700">Original Price: ${originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-yellow-300 text-xl font-bold">Promo Price: ${discountedPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-yellow-400 font-semibold flex justify-center items-center gap-1">
                  <Wallet className="w-4 h-4" /> You Save: ${savings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            {discount && revealCode ? (
              <a
                href={getAffiliateUrl(firm.id)}
                target="_blank"
                rel="noopener"
                className="flex-1 bg-yellow-400 text-black px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold hover:bg-yellow-300 transition text-center text-sm sm:text-base w-full sm:w-auto focus:ring-2 focus:ring-yellow-400 focus:outline-none active:scale-95 whitespace-nowrap"
              >
                {discount.code}
              </a>
            ) : discount ? (
              <button
                onClick={() => setRevealCode(true)}
                className="flex-1 bg-yellow-400 text-black px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold hover:bg-yellow-300 transition text-center text-sm sm:text-base w-full sm:w-auto focus:ring-2 focus:ring-yellow-400 focus:outline-none active:scale-95 whitespace-nowrap"
              >
                Click to reveal code
              </button>
            ) : null}

            <a
              href={`/reviews/${firm.id}`}
              className="flex-1 bg-white text-yellow-700 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-center hover:bg-yellow-100 transition text-sm sm:text-base w-full sm:w-auto whitespace-nowrap"
            >
              Read Review
            </a>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={onPrev}
              className="bg-white text-blue-800 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-blue-50 transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={onNext}
              className="bg-white text-blue-800 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-blue-50 transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeroCarouselCard;